import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Skeleton,
} from '@mui/material';
import {
  PersonRemove as RemoveIcon,
  PersonAdd as AddIcon,
  People as PeopleIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  notificationChannelApi,
  ListMember,
  ContactSearchResult,
} from '../../../../../api/cansense/notificationChannelApi';

// ============================================================================
// Types
// ============================================================================

interface SubscriberManagementProps {
  listId: number;
  readOnly?: boolean;
}

// ============================================================================
// Component
// ============================================================================

/**
 * SubscriberManagement - View and manage subscribers on a CANmail distribution list.
 *
 * Self-fetching component that loads members on mount.
 * Features:
 * - Member list table (name + email)
 * - Contact search autocomplete for adding members
 * - Per-row remove with loading state
 * - Optimistic local state updates
 */
export const SubscriberManagement: React.FC<SubscriberManagementProps> = ({
  listId,
  readOnly = false,
}) => {
  const theme = useTheme();
  const mountedRef = useRef(true);

  // Member list state
  const [members, setMembers] = useState<ListMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [listName, setListName] = useState('');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ContactSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Action loading state
  const [removingContactId, setRemovingContactId] = useState<number | null>(null);
  const [addingContactId, setAddingContactId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Fetch members
  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await notificationChannelApi.getListMembers(listId);
      if (mountedRef.current) {
        setMembers(data.members);
        setListName(data.listName);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load members');
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [listId]);

  useEffect(() => {
    mountedRef.current = true;
    fetchMembers();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchMembers]);

  // Debounced contact search
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const results = await notificationChannelApi.searchContacts(searchQuery);
        if (mountedRef.current) {
          // Filter out contacts already in the list
          const memberIds = new Set(members.map((m) => m.contactId));
          setSearchResults(results.filter((r) => !memberIds.has(r.contactId)));
        }
      } catch {
        // Silently handle search errors
      } finally {
        if (mountedRef.current) {
          setSearchLoading(false);
        }
      }
    }, 300);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchQuery, members]);

  // Add member handler
  const handleAddMember = async (contact: ContactSearchResult) => {
    setAddingContactId(contact.contactId);
    setActionError(null);
    try {
      await notificationChannelApi.addListMember(listId, contact.contactId);
      setMembers((prev) => [
        ...prev,
        {
          contactId: contact.contactId,
          firstName: contact.firstName,
          lastName: contact.lastName,
          email: contact.email,
        },
      ]);
      setSearchQuery('');
      setSearchResults([]);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to add member');
    } finally {
      setAddingContactId(null);
    }
  };

  // Remove member handler
  const handleRemoveMember = async (contactId: number) => {
    setRemovingContactId(contactId);
    setActionError(null);
    try {
      await notificationChannelApi.removeListMember(listId, contactId);
      setMembers((prev) => prev.filter((m) => m.contactId !== contactId));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to remove member');
    } finally {
      setRemovingContactId(null);
    }
  };

  // Loading skeleton
  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width="40%" height={32} />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 1, borderRadius: 1 }} />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PeopleIcon sx={{ color: 'text.secondary' }} />
          <Typography variant="subtitle2">
            {listName ? `${listName} Members` : 'List Members'}
          </Typography>
          <Chip label={members.length} size="small" variant="outlined" />
        </Box>
      </Box>

      {/* Search / Add member */}
      {!readOnly && (
        <Autocomplete
          freeSolo
          open={searchOpen && searchResults.length > 0}
          onOpen={() => setSearchOpen(true)}
          onClose={() => setSearchOpen(false)}
          options={searchResults}
          getOptionLabel={(option) =>
            typeof option === 'string'
              ? option
              : `${option.firstName} ${option.lastName} (${option.email})`
          }
          inputValue={searchQuery}
          onInputChange={(_, value) => setSearchQuery(value)}
          onChange={(_, value) => {
            if (value && typeof value !== 'string') {
              handleAddMember(value);
            }
          }}
          loading={searchLoading}
          renderOption={(props, option) => (
            <li {...props} key={option.contactId}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">
                    {option.firstName} {option.lastName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.email}
                  </Typography>
                </Box>
                {addingContactId === option.contactId ? (
                  <CircularProgress size={20} />
                ) : (
                  <AddIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                )}
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search contacts to add..."
              size="small"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{ mb: 2 }}
        />
      )}

      {/* Action error */}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {/* Members table */}
      {members.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <PeopleIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No members in this list yet.
          </Typography>
          {!readOnly && (
            <Typography variant="caption" color="text.secondary">
              Use the search above to add contacts.
            </Typography>
          )}
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.grey[500], 0.04) }}>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                {!readOnly && (
                  <TableCell align="right" sx={{ width: 60 }}>
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((member) => (
                <TableRow key={member.contactId} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {member.firstName} {member.lastName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {member.email}
                    </Typography>
                  </TableCell>
                  {!readOnly && (
                    <TableCell align="right">
                      <Tooltip title="Remove from list">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveMember(member.contactId)}
                          disabled={removingContactId === member.contactId}
                        >
                          {removingContactId === member.contactId ? (
                            <CircularProgress size={16} />
                          ) : (
                            <RemoveIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

export default SubscriberManagement;
